import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RefreshCw, Filter, FileText, User, Calendar, Building2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [filterAdmin, setFilterAdmin] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  const actionTypes = [
    { value: 'all', label: 'Semua Aktivitas' },
    { value: 'CHECK_IN', label: 'Check-in' },
    { value: 'CHECK_OUT', label: 'Check-out' },
    { value: 'CREATE_ROOM', label: 'Tambah Kamar' },
    { value: 'DELETE_ROOM', label: 'Hapus Kamar' },
    { value: 'CREATE_TRANSACTION', label: 'Input Transaksi' },
    { value: 'DELETE_TRANSACTION', label: 'Hapus Transaksi' }
  ];

  useEffect(() => {
    fetchAdmins();
    fetchLogs();
  }, []);

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${BACKEND_URL}/api/admin/admins`, config);
      setAdmins(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      let url = `${BACKEND_URL}/api/admin/activity-logs?limit=200`;
      
      if (filterAdmin && filterAdmin !== 'all') {
        url += `&admin_email=${encodeURIComponent(filterAdmin)}`;
      }
      if (filterAction && filterAction !== 'all') {
        url += `&action=${filterAction}`;
      }
      if (filterStartDate) {
        url += `&start_date=${filterStartDate}`;
      }
      if (filterEndDate) {
        url += `&end_date=${filterEndDate}`;
      }
      
      const response = await axios.get(url, config);
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    fetchLogs();
  };

  const resetFilters = () => {
    setFilterAdmin('all');
    setFilterAction('all');
    setFilterStartDate('');
    setFilterEndDate('');
    setTimeout(() => fetchLogs(), 100);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'CHECK_IN':
        return 'bg-isk-teal/10 text-isk-teal';
      case 'CHECK_OUT':
        return 'bg-red-100 text-red-700';
      case 'CREATE_ROOM':
        return 'bg-isk-teal/10 text-isk-teal';
      case 'DELETE_ROOM':
        return 'bg-orange-100 text-orange-700';
      case 'CREATE_TRANSACTION':
        return 'bg-purple-100 text-purple-700';
      case 'DELETE_TRANSACTION':
        return 'bg-pink-100 text-pink-700';
      default:
        return 'bg-isk-bg text-isk-muted';
    }
  };

  const getActionLabel = (action) => {
    const found = actionTypes.find(a => a.value === action);
    return found ? found.label : action;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-isk-text">Log Aktivitas Admin</h2>
          <p className="text-sm text-isk-muted mt-1">Pantau semua aktivitas yang dilakukan oleh admin</p>
        </div>
        <Button
          onClick={fetchLogs}
          variant="outline"
          className="border-isk-teal/30 text-isk-teal hover:bg-isk-teal hover:text-white rounded-full"
          data-testid="refresh-logs"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="glass-card rounded-2xl p-6 mb-6">
        <div className="flex items-center mb-4">
          <Filter className="w-5 h-5 text-isk-teal mr-2" />
          <h3 className="text-lg font-semibold text-isk-text">Filter Log</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label className="text-isk-text font-semibold">Admin</Label>
            <Select value={filterAdmin} onValueChange={setFilterAdmin}>
              <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl" data-testid="filter-admin">
                <SelectValue placeholder="Semua Admin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Admin</SelectItem>
                {admins.map((admin, idx) => (
                  <SelectItem key={idx} value={admin.email}>
                    {admin.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-isk-text font-semibold">Jenis Aktivitas</Label>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl" data-testid="filter-action">
                <SelectValue placeholder="Semua Aktivitas" />
              </SelectTrigger>
              <SelectContent>
                {actionTypes.map((action, idx) => (
                  <SelectItem key={idx} value={action.value}>
                    {action.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-isk-text font-semibold">Dari Tanggal</Label>
            <Input
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              className="glass-input border-2 focus:border-isk-teal rounded-xl"
              data-testid="filter-start-date"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-isk-text font-semibold">Sampai Tanggal</Label>
            <Input
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              className="glass-input border-2 focus:border-isk-teal rounded-xl"
              data-testid="filter-end-date"
            />
          </div>
        </div>

        <div className="flex space-x-3 mt-4">
          <Button
            onClick={handleFilter}
            className="bg-isk-teal hover:bg-isk-dark text-white rounded-full px-6"
            data-testid="apply-filter"
          >
            Terapkan Filter
          </Button>
          <Button
            onClick={resetFilters}
            variant="outline"
            className="border-isk-teal/30 text-isk-text rounded-full px-6"
            data-testid="reset-filter"
          >
            Reset
          </Button>
        </div>
      </div>

      {/* Logs List */}
      <div className="glass-card rounded-2xl p-6">
        <div className="flex items-center mb-4">
          <FileText className="w-5 h-5 text-isk-teal mr-2" />
          <h3 className="text-lg font-semibold text-isk-text">
            Riwayat Aktivitas ({logs.length} log)
          </h3>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-isk-muted">Memuat log...</p>
          </div>
        ) : logs.length > 0 ? (
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {logs.map((log, index) => (
              <div 
                key={index}
                className="p-4 rounded-xl bg-white/50 border border-white/20 hover:bg-white/70 transition-colors"
                data-testid={`log-entry-${index}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getActionBadgeColor(log.action)}`}>
                      {getActionLabel(log.action)}
                    </span>
                    <span className="text-xs text-isk-muted">
                      {formatTimestamp(log.timestamp)}
                    </span>
                  </div>
                </div>
                
                <p className="text-sm text-isk-text font-medium mb-2">{log.details}</p>
                
                <div className="flex items-center space-x-4 text-xs text-isk-muted">
                  <span className="flex items-center">
                    <User className="w-3 h-3 mr-1" />
                    {log.admin_name} ({log.admin_email})
                  </span>
                  {log.property_name && (
                    <span className="flex items-center">
                      <Building2 className="w-3 h-3 mr-1" />
                      {log.property_name}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-isk-muted">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Belum ada log aktivitas</p>
            <p className="text-sm">Log akan muncul saat admin melakukan aktivitas</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogViewer;
